










































































































































































































































































































































































































































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { getActiveUser } from "@/data/repository/UserRepository";
import { IresDevice } from "@/data/models/IresDevice";
import { ProgramChain } from "@/data/models/ProgramChain";
import { firestore } from "firebase/app";

import { AiPredictionApi, AiPredictionApiError } from "@/api/AiPredictionApi";
import {
  PredictionsResponse,
  ManualPredictResponse,
  WindowResult,
  WindowPredictions,
  TrafficLight,
  PredictionWindow,
  CorrectionPayload,
  SaveCorrectionRequest,
  WindowCorrection,
  ManualLabels,
  BinaryLabelCorrections,
  TimelineMarkerPoint,
  TrendEntry,
  getTrueProbability,
  isBooleanPredictionTrue,
  extractWindowResults,
  normalizeWindowPredictions,
} from "@/data/models/AiPrediction";

import ProbabilityBarComponent from "@/components/common/ProbabilityBar.vue";
import { getDailyPredictionByExecutionId } from "@/data/repository/AiPredictionRepository";
import { COLOR_APP_BAR_DARK, COLOR_PRIMARY, COLOR_SURFACE_DARK, COLOR_TEXT_DARK } from "@/utils/colors";
import { themed } from "@/utils/Theme";
import { formatDateHoursMinutes, formatDate } from "@/utils/DateFormatter";
import { UserModule } from "@/store/modules/UserModule";
import { getAiPredictionsAccess } from "@/data/repository/ClientRepository";

/**
 * Program numbers RENO serves, mirroring the backend's program-group routing
 * (`config.PROGRAM_GROUPS` x `config.SERVING_ENABLED_GROUPS` in IRES Assist):
 * 0/1/2 = EDT, 3 = monitoring, 8 = raumtrocknung. A chain outside this set is
 * rejected by the backend with `unsupported_program`, so Office hides the view
 * rather than issuing a request that cannot succeed.
 *
 * The groups do not all predict the same targets, so the per-target rows below
 * derive their "has an AI model" flag from what the payload actually contains.
 */
const SUPPORTED_PROGRAM_NUMBERS = new Set([0, 1, 2, 3, 8]);

/**
 * Convert a value that may be a Firestore Timestamp, ISO string, or Date to a Date.
 * Handles both v1 (Firestore Timestamp) and v2 (ISO string) schema formats.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toDateSafe(value: any): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value === "string" || typeof value === "number") return new Date(value);
  if (value._seconds != null) return new Date(value._seconds * 1000);
  return null;
}

/**
 * Internal correction state that tracks user edits.
 */
interface CorrectionState {
  trafficLight: TrafficLight;
  isDry: boolean;
  isStationary: boolean;
  binaryLabels: Record<string, boolean>;
  manualLabels: Record<string, boolean>;
}

/**
 * Item for the labeled checkbox rows (Row 4).
 */
interface LabelItem {
  key: string;
  label: string;
  hasAi: boolean;
  probability: number;
  storeKey: "binaryLabels" | "manualLabels";
}

@Component({
  components: {
    "probability-bar": ProbabilityBarComponent,
  },
})
export default class AiPredictions extends Vue {
  // ─── Props (matching RateDrying pattern) ──────────────────────────────

  @Prop()
  iresDevice!: IresDevice;

  @Prop()
  programChain!: ProgramChain;

  /** Selected trend entry from time series click (null = show latest for active drying, empty for finished). */
  @Prop({ default: null })
  selectedEntry!: TrendEntry | null;

  /** Whether the current drying is still active. */
  @Prop({ default: false })
  isDryingActive!: boolean;

  /** Whether the RENO time series is visible above this component. */
  @Prop({ default: false })
  timeSeriesVisible!: boolean;

  // ─── Derived from Props ──────────────────────────────────────────────

  /**
   * Phones (below 600 px): the tabs take the header row with the pickers under them, each label is
   * its checkbox with the probability bar full width beneath it, and the two actions stack.
   */
  get isPhone() {
    return this.$vuetify.breakpoint.xsOnly;
  }

  /** The label rows' probability column is 200 px wide beside the label; on phones it is the row's width. */
  get labelProbabilityStyle() {
    return this.isPhone ? null : { minWidth: "200px" };
  }

  /**
   * Scrim color for the manual-prediction loading overlay: a light scrim over
   * the (white) card in light mode, a dark surface in dark mode so the overlay
   * dims rather than washes out the underlying content.
   */
  get manualOverlayColor(): string {
    return themed("white", COLOR_SURFACE_DARK);
  }

  /** Body-text color inside the loading overlay, inverted to stay legible per theme. */
  get manualOverlayTextColor(): string {
    return themed("black", COLOR_TEXT_DARK);
  }

  get chainId(): string {
    return this.programChain?.id ?? "";
  }

  get serialNo(): string {
    return this.iresDevice?.serialNo ?? "";
  }

  get programNumber(): number {
    return this.programChain?.runs?.[0]?.programNumber ?? 1;
  }

  get isProgramSupported(): boolean {
    return SUPPORTED_PROGRAM_NUMBERS.has(this.programNumber);
  }

  // ─── Model Version ──────────────────────────────────────────────────

  /**
   * Available RENO model versions for this chain, sorted ascending.
   */
  get availableModelVersions(): string[] {
    return this.predictionsData?.availableModelVersions ?? [];
  }

  get isPredictionFeatureActivated(): boolean {
    return getAiPredictionsAccess() ?? false;
  }

  // ─── Data ────────────────────────────────────────────────────────────
  predictionsData: PredictionsResponse | null = null;
  manualResult: ManualPredictResponse | null = null;

  // ─── UI State ────────────────────────────────────────────────────────
  isLoading = false;
  isSavingCorrection = false;
  isManualPredictionRunning = false;
  isBackfillRunning = false;
  private backfillPollTimer: number | null = null;
  activeViewTab = 0;
  selectedWindow = "all";
  selectedModelVersion = "";
  showManualCalculationDialog = false;
  manualWindows: (number | "all")[] = [];
  editedFields: Set<string> = new Set();
  loadError = "";

  // ─── Snackbar ────────────────────────────────────────────────────────
  showSuccessSnackbar = false;
  showErrorSnackbar = false;
  successMessage = "";
  errorMessage = "";
  snackbarTimeout = 5000;

  // ─── Timeline Marker State ───────────────────────────────────────────
  timelineStationaryAt: TimelineMarkerPoint | null = null;
  timelineDryAt: TimelineMarkerPoint | null = null;

  // ─── Correction State ────────────────────────────────────────────────
  correctionState: CorrectionState = {
    trafficLight: "UNKNOWN",
    isDry: false,
    isStationary: false,
    binaryLabels: {
      powerFailure: false,
      newMoisture: false,
      slowDrying: false,
      unfavorableConditions: false,
      lowInitialMoisture: false,
      relayDefect: false,
    },
    manualLabels: {
      ambientHumidityBad: false,
      notForTraining: false,
      clearClass: false,
    },
  };

  // ─── Computed: Active Window Result ──────────────────────────────────

  get activeView(): "daily" | "manual" {
    return this.activeViewTab === 0 ? "daily" : "manual";
  }

  /**
   * The selected tab: the app bar colour in both themes (COLOR_PRIMARY / COLOR_APP_BAR_DARK). The
   * Vuetify underline takes the same colour (`slider-color`), so, as in light mode, it never shows
   * as a line of its own.
   */
  get activeTabColor(): string {
    return themed(COLOR_PRIMARY, COLOR_APP_BAR_DARK);
  }

  /**
   * Gets the window result currently being viewed.
   */
  get activeWindowResult(): WindowResult | null {
    if (!this.predictionsData) return null;

    if (this.activeView === "daily") {
      return this.getDailyWindowResult();
    } else {
      return this.getManualWindowResult();
    }
  }

  /**
   * Gets a daily window result for the selected window.
   */
  private getDailyWindowResult(): WindowResult | null {
    const daily = this.predictionsData?.daily;
    if (!daily) return null;

    const windows = extractWindowResults(daily.results.windows);
    const target = this.selectedWindow;

    return (
      windows.find((w) => String(w.window) === target) ??
      windows[0] ??
      null
    );
  }

  /**
   * Gets a manual window result for the selected window.
   */
  private getManualWindowResult(): WindowResult | null {
    const manual = this.predictionsData?.manual;
    if (!manual) return null;

    // Manual results are stored as a map keyed by window string
    const windowMap = manual.results.windows;
    return windowMap[this.selectedWindow] ?? Object.values(windowMap)[0] ?? null;
  }

  // ─── Computed: Predictions ───────────────────────────────────────────

  /**
   * Page title following the format "RENO Tagesauswertung: DD.MM.YY, Tag nn".
   * Falls back to the simple "RENO" label when no prediction date is available.
   */
  get pageTitle(): string {
    const predictionDate = this.activePredictionDate;
    if (!predictionDate || this.awaitingDaySelection) return "RENO";

    const dateStr = formatDate(predictionDate, "dd.MM.yy");
    const dayNumber = this.dryingDayNumber;
    const dayStr = dayNumber > 0 ? `, Tag ${dayNumber}` : "";
    return `RENO Tagesauswertung: ${dateStr}${dayStr}`;
  }

  /**
   * The date of the currently active prediction (the day it covers, not when it was computed).
   * - selectedEntry: use the entry's date (clicked from time series).
   * - Active drying (no entry): today's date (latest prediction covers today).
   * - Manual: current date (on-demand).
   */
  get activePredictionDate(): Date | null {
    if (this.selectedEntry) {
      const d = new Date(this.selectedEntry.date);
      return isNaN(d.getTime()) ? null : d;
    }
    if (this.activeView === "daily") {
      if (!this.predictionsData?.daily) return null;
      // For active dryings, the latest daily prediction covers "today"
      if (this.isDryingActive) return new Date();
      // For historical dryings with data loaded (non-RENO), use createdAt as fallback
      const raw = this.predictionsData.daily.createdAt;
      const d = toDateSafe(raw);
      return d && !isNaN(d.getTime()) ? d : null;
    }
    // Manual predictions don't have a stored date — they were just triggered
    return this.predictionsData?.manual ? new Date() : null;
  }

  /**
   * Which drying day the prediction covers, relative to chain start.
   */
  get dryingDayNumber(): number {
    const predDate = this.activePredictionDate;
    const startTs = this.programChain?.startDate;
    if (!predDate || !startTs) return 0;
    // Use calendar days: start day = day 0, first full day = day 1
    const startDate = startTs.toDate();
    const startMidnight = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const predMidnight = new Date(predDate.getFullYear(), predDate.getMonth(), predDate.getDate());
    const diffDays = Math.round((predMidnight.getTime() - startMidnight.getTime()) / 86_400_000);
    return Math.max(1, diffDays);
  }

  get activeModelVersion(): string {
    // Use selected version from picker, fall back to document-level modelVersion
    if (this.selectedModelVersion) return this.selectedModelVersion;
    if (this.activeView === "daily") {
      return this.predictionsData?.daily?.modelVersion
        ?? this.activeWindowResult?.modelSet
        ?? this.predictionsData?.daily?.modelSet
        ?? "";
    }
    return this.predictionsData?.manual?.modelVersion
      ?? this.activeWindowResult?.modelSet
      ?? "";
  }

  /**
   * Whether a newer model version is available but NO data has been calculated with it yet.
   * Does NOT trigger when the user simply views an older model version in the picker.
   */
  get newerModelAvailable(): boolean {
    const latest = this.predictionsData?.latestAvailableModel;
    if (!latest) return false;
    // If the available data already includes the latest model, no backfill needed
    return !this.availableModelVersions.includes(latest);
  }

  /**
   * Recalculation is only allowed when the latest available model is selected.
   * If the backend does not provide a latest marker, keep actions enabled.
   */
  get isLatestModelSelected(): boolean {
    const latest = this.predictionsData?.latestAvailableModel;
    if (!latest) return true;

    if (!this.availableModelVersions.includes(latest)) {
      return this.activeModelVersion === this.getHighestAvailableModelVersion();
    }

    return this.activeModelVersion === latest;
  }

  /**
   * Whether the latest available model is a RENO version (R1.0+).
   * For RENO models, manual predictions always use window "all" only.
   */
  get isRenoModel(): boolean {
    const latest = this.predictionsData?.latestAvailableModel ?? this.activeModelVersion;
    return latest.startsWith("R");
  }

  /**
   * Whether the selected model version is a RENO version (time series available).
   */
  get isSelectedModelReno(): boolean {
    return this.selectedModelVersion.startsWith("R");
  }

  /**
   * True when this is a historical drying with a RENO model selected, the time series
   * is visible, and no specific day has been picked yet.
   */
  get awaitingDaySelection(): boolean {
    return !this.isDryingActive && this.isSelectedModelReno && this.timeSeriesVisible && !this.selectedEntry;
  }

  get predictions(): WindowPredictions | null {
    const raw = this.activeWindowResult?.predictions;
    if (!raw) return null;
    return normalizeWindowPredictions(raw);
  }

  get binaryPredictions(): { abnormal: number; normal: number } | null {
    if (!this.predictions?.binary) return null;
    const probs = this.predictions.binary.probabilities;
    return {
      abnormal: probs["abnormal"] ?? probs["anormal"] ?? 0,
      normal: probs["normal"] ?? 0,
    };
  }

  get dryProbability(): number | null {
    if (!this.predictions?.dry) return null;
    return getTrueProbability(this.predictions.dry);
  }

  get stationaryProbability(): number | null {
    if (!this.predictions?.stationary) return null;
    return getTrueProbability(this.predictions.stationary);
  }

  // ─── Computed: Label Items (Row 4) ───────────────────────────────────

  /**
   * Whether the active window actually carries a prediction for `targetKey`.
   *
   * Not every program group predicts every target: monitoring (program 3) has no
   * `slowDrying`/`relayDefect`/`lowInitialMoisture` model, and raumtrocknung
   * (program 8) predicts an entirely different set. An absent target must read as
   * "no AI model" rather than as a 0 % probability, which would claim the model
   * ran and found nothing.
   */
  private hasPredictionFor(targetKey: keyof WindowPredictions): boolean {
    return this.predictions?.[targetKey] != null;
  }

  get labelItemsGroupA(): LabelItem[] {
    const p = this.predictions;
    return [
      {
        key: "powerFailure",
        label: this.$t("quality.labels.powerFailure").toString(),
        hasAi: this.hasPredictionFor("powerOutage"),
        probability: p ? getTrueProbability(p.powerOutage) : 0,
        storeKey: "binaryLabels",
      },
      {
        key: "newMoisture",
        label: this.$t("quality.labels.newMoisture").toString(),
        hasAi: this.hasPredictionFor("newMoistureIngress"),
        probability: p ? getTrueProbability(p.newMoistureIngress) : 0,
        storeKey: "binaryLabels",
      },
      {
        key: "slowDrying",
        label: this.$t("quality.labels.slowDrying").toString(),
        hasAi: this.hasPredictionFor("slowDrying"),
        probability: p ? getTrueProbability(p.slowDrying) : 0,
        storeKey: "binaryLabels",
      },
      {
        key: "unfavorableConditions",
        label: this.$t("quality.labels.unfavorableConditions").toString(),
        hasAi: this.hasPredictionFor("unfavorableAmbient"),
        probability: p ? getTrueProbability(p.unfavorableAmbient) : 0,
        storeKey: "binaryLabels",
      },
      {
        key: "ambientHumidityBad",
        label: this.$t("quality.labels.ambientHumidityBad").toString(),
        hasAi: this.hasPredictionFor("unfavorableAmbient"),
        probability: p ? getTrueProbability(p.unfavorableAmbient) : 0,
        storeKey: "manualLabels",
      },
      {
        key: "lowInitialMoisture",
        label: this.$t("quality.labels.lowInitialMoisture").toString(),
        hasAi: this.hasPredictionFor("lowInitialMoisture"),
        probability: p ? getTrueProbability(p.lowInitialMoisture) : 0,
        storeKey: "binaryLabels",
      },
    ];
  }

  get labelItemsGroupB(): LabelItem[] {
    const p = this.predictions;
    return [
      {
        key: "relayDefect",
        label: this.$t("quality.labels.relayDefect").toString(),
        hasAi: this.hasPredictionFor("relayDefect"),
        probability: p ? getTrueProbability(p.relayDefect) : 0,
        storeKey: "binaryLabels",
      },
      {
        key: "notForTraining",
        label: this.$t("quality.labels.notForTraining").toString(),
        hasAi: false,
        probability: 0,
        storeKey: "manualLabels",
      },
      {
        key: "clearClass",
        label: this.$t("quality.labels.clearClass").toString(),
        hasAi: false,
        probability: 0,
        storeKey: "manualLabels",
      },
    ];
  }

  // ─── Computed: Windows ───────────────────────────────────────────────

  get availableWindows(): string[] {
    if (!this.predictionsData) return [];

    if (this.activeView === "daily") {
      const daily = this.predictionsData.daily;
      if (!daily) return ["all"];
      return extractWindowResults(daily.results.windows).map((w) => String(w.window));
    } else {
      const manual = this.predictionsData.manual;
      if (!manual) return [];
      return Object.keys(manual.results.windows);
    }
  }

  get windowItems() {
    return this.availableWindows.map((w) => ({
      text: w === "all" ? this.$t("quality.windows.all") : this.$t("quality.windows.days", [w]),
      value: w,
    }));
  }

  get allWindowOptions() {
    return [
      { value: 3, label: this.$t("quality.windows.days", [3]).toString() },
      { value: 6, label: this.$t("quality.windows.days", [6]).toString() },
      { value: 9, label: this.$t("quality.windows.days", [9]).toString() },
      { value: 12, label: this.$t("quality.windows.days", [12]).toString() },
      { value: "all" as const, label: this.$t("quality.windows.all").toString() },
    ];
  }

  // ─── Computed: State ─────────────────────────────────────────────────

  get hasManualResult(): boolean {
    return this.predictionsData?.manual !== null && this.predictionsData?.manual !== undefined;
  }

  get hasEdits(): boolean {
    return this.editedFields.size > 0;
  }

  get hasCorrectionForCurrentWindow(): boolean {
    return !!this.getCurrentCorrection();
  }

  get correctionInfo(): { correctedAt: string; correctedBy: string } | null {
    const windowCorrection = this.getCurrentCorrection();
    if (!windowCorrection) return null;

    return {
      correctedAt: formatDateHoursMinutes(new Date(windowCorrection.correctedAt)),
      correctedBy: windowCorrection.correctedBy
        ? UserModule.userNickname(windowCorrection.correctedBy)
        : "",
    };
  }

  /**
   * The execution ID of the prediction currently being viewed.
   * Used when saving corrections to identify which prediction was corrected.
   */
  get currentExecutionId(): string {
    if (this.activeView === "daily") {
      return this.predictionsData?.daily?.executionId ?? "";
    }
    return this.predictionsData?.manual?.executionId ?? "manual";
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────

  async created() {
    if (!this.chainId || !this.serialNo) {
      return;
    }

    if (!this.isProgramSupported) {
      return;
    }

    await this.loadPredictions();
  }

  beforeDestroy() {
    this.stopBackfillPolling();
  }

  /**
   * Watch chainId (a string) instead of the programChain object reference.
   * Firestore real-time listeners recreate ProgramChain objects on every
   * snapshot, causing the object reference to change even when the chain
   * hasn't actually changed. Comparing by chainId (string equality) avoids
   * unnecessary reloads and loading-animation flashes.
   */
  @Watch("chainId")
  async onChainIdChanged() {
    this.predictionsData = null;
    this.selectedModelVersion = "";
    this.editedFields.clear();
    this.emitPredictionState();

    if (!this.isProgramSupported) {
      return;
    }

    await this.loadPredictions();
  }

  @Watch("activeViewTab")
  onActiveViewTabChanged() {
    this.emitPredictionState();
  }

  @Watch("selectedWindow")
  onSelectedWindowChanged() {
    this.emitPredictionState();
  }

  @Watch("selectedModelVersion")
  onModelVersionChanged() {
    this.$emit("modelVersionChanged", this.selectedModelVersion);
  }

  @Watch("selectedEntry")
  async onSelectedEntryChanged() {
    if (!this.selectedEntry) return;
    await this.loadPredictionForEntry(this.selectedEntry);
  }

  /**
   * Resolves the default model version to show/select.
   * Prefers backend-provided latest marker, falls back to the highest
   * available model version from the response.
   */
  private getHighestAvailableModelVersion(): string {
    const versions = this.availableModelVersions;
    if (versions.length === 0) {
      return "";
    }

    const sorted = [...versions].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    return sorted[sorted.length - 1] ?? "";
  }

  private getDefaultModelVersion(): string {
    const latestFromBackend = this.predictionsData?.latestAvailableModel?.trim() ?? "";

    if (latestFromBackend && this.availableModelVersions.includes(latestFromBackend)) {
      return latestFromBackend;
    }

    return this.getHighestAvailableModelVersion();
  }

  // ─── Data Loading ────────────────────────────────────────────────────

  /**
   * Loads all prediction data from the API in a single call.
   */
  async loadPredictions() {
    this.isLoading = true;
    this.loadError = "";

    try {
      this.predictionsData = await AiPredictionApi.getPredictions(
        this.chainId,
        this.serialNo
      );

      const latestModel = this.getDefaultModelVersion();

      // Always default to the latest model when loading predictions for the
      // quality view. This avoids stale older selections (e.g. R1.0) carrying
      // over when latest is available (e.g. R2.0).
      if (latestModel) {
        this.selectedModelVersion = latestModel;
      }

      this.initializeCorrectionState();
      this.emitPredictionState();
    } catch (error) {
      if (error instanceof AiPredictionApiError) {
        this.loadError = error.apiMessage;
      } else {
        this.loadError = this.$t("quality.messages.noData").toString();
      }
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Loads a specific day's prediction from Firestore by executionId.
   * Used when a user clicks a day in the time series.
   */
  async loadPredictionForEntry(entry: TrendEntry) {
    this.isLoading = true;
    this.loadError = "";

    try {
      const daily = await getDailyPredictionByExecutionId(
        this.serialNo,
        this.chainId,
        entry.executionId
      );

      if (daily) {
        // Merge into predictionsData keeping existing metadata
        this.predictionsData = {
          chainId: this.chainId,
          serialNo: this.serialNo,
          daily,
          manual: this.predictionsData?.manual ?? null,
          correction: this.predictionsData?.correction ?? null,
          summary: this.predictionsData?.summary ?? {
            daily: {
              trafficLight: daily.results.final.finalTrafficLight,
              isDry: daily.results.final.isDry ?? false,
              isStationary: daily.results.final.isStationary ?? false,
              latestExecutionId: daily.executionId,
              source: "daily" as const,
            },
            latest: {
              trafficLight: daily.results.final.finalTrafficLight,
              source: "daily" as const,
            },
          },
          availableModelVersions: this.predictionsData?.availableModelVersions,
          latestAvailableModel: this.predictionsData?.latestAvailableModel,
        };

        const latestModel = this.getDefaultModelVersion();
        if (latestModel) {
          this.selectedModelVersion = latestModel;
        }

        this.activeViewTab = 0; // Switch to daily view
        this.initializeCorrectionState();
        this.emitPredictionState();
      } else {
        this.loadError = this.$t("quality.messages.noData").toString();
      }
    } catch {
      this.loadError = this.$t("quality.messages.noData").toString();
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Emits the current prediction state so the parent can decide whether
   * to show timeline markers and what end-time to use (1.00 = last prediction).
   */
  private emitPredictionState() {
    const hasDaily = !!this.predictionsData?.daily;
    const hasManual = !!this.predictionsData?.manual;
    const isAllWindow = this.selectedWindow === "all";

    // Show timeline markers for: daily view, or manual view with "all" window
    const showTimeline = (hasDaily && this.activeView === "daily") ||
                         (hasManual && this.activeView === "manual" && isAllWindow);

    let lastPredictionDate: Date | null = null;
    if (this.predictionsData?.daily?.createdAt) {
      lastPredictionDate = toDateSafe(this.predictionsData.daily.createdAt);
    }

    this.$emit("predictionStateChanged", {
      showTimeline,
      lastPredictionDate,
      hasPredictionData: !!this.predictionsData,
      latestAvailableModel: this.predictionsData?.latestAvailableModel ?? "",
    });
  }

  /**
   * Resolve the correction entry for the current UI context.
   *
   * - RENO daily context: by daily execution ID (day-specific)
   * - Legacy/manual context: by trigger + window (window-specific)
   */
  private getCurrentCorrection(): WindowCorrection | null {
    const correctionsMap = this.predictionsData?.correction?.corrections;
    if (!correctionsMap) return null;

    const entries = Object.values(correctionsMap).filter(
      (entry): entry is WindowCorrection => !!entry
    );
    if (entries.length === 0) return null;

    if (this.activeView === "daily" && this.isSelectedModelReno) {
      const executionId = this.currentExecutionId;
      if (!executionId) return null;

      return (
        entries.find(
          (entry) =>
            entry.sourceTrigger === "daily" &&
            entry.sourceExecutionId === executionId
        ) ?? correctionsMap[`daily:${executionId}`] ?? null
      );
    }

    const trigger = this.activeView;
    return (
      entries.find(
        (entry) =>
          entry.sourceTrigger === trigger &&
          String(entry.window ?? "") === this.selectedWindow
      ) ?? correctionsMap[`${trigger}:${this.selectedWindow}`] ?? correctionsMap[this.selectedWindow] ?? null
    );
  }

  // ─── Correction State Management ────────────────────────────────────

  /**
   * Initialize the correction form state from the active window result.
   * If corrections exist for this window, overlay them on top.
   */
  initializeCorrectionState() {
    const windowResult = this.activeWindowResult;

    // Set from prediction
    if (windowResult) {
      this.correctionState.trafficLight = windowResult.trafficLight ?? "UNKNOWN";
      this.correctionState.isDry = windowResult.isDry ?? false;
      this.correctionState.isStationary = windowResult.isStationary ?? false;

      // Initialize binary labels from predictions
      const p = windowResult.predictions;
      if (p) {
        this.correctionState.binaryLabels.lowInitialMoisture = isBooleanPredictionTrue(p.lowInitialMoisture);
        this.correctionState.binaryLabels.newMoisture = isBooleanPredictionTrue(p.newMoistureIngress);
        this.correctionState.binaryLabels.slowDrying = isBooleanPredictionTrue(p.slowDrying);
        this.correctionState.binaryLabels.unfavorableConditions = isBooleanPredictionTrue(p.unfavorableAmbient);
        this.correctionState.manualLabels.ambientHumidityBad = isBooleanPredictionTrue(p.unfavorableAmbient);
        this.correctionState.binaryLabels.relayDefect = isBooleanPredictionTrue(p.relayDefect);
      }
    } else if (this.predictionsData?.summary) {
      this.correctionState.trafficLight = this.predictionsData.summary.latest?.trafficLight ?? "UNKNOWN";
    }

    // Overlay corrections if they exist and match the active view
    const correction = this.getCurrentCorrection();
    if (correction) {
      this.overlayCorrectionValues(correction);
    }

    this.editedFields.clear();
  }

  /**
   * Overlays existing correction values onto the form state.
   */
  private overlayCorrectionValues(correction: WindowCorrection) {
    if (correction.trafficLight) {
      this.correctionState.trafficLight = correction.trafficLight;
    }
    if (correction.isDry !== undefined) {
      this.correctionState.isDry = correction.isDry;
    }
    if (correction.isStationary !== undefined) {
      this.correctionState.isStationary = correction.isStationary;
    }
    if (correction.binaryLabels) {
      Object.assign(this.correctionState.binaryLabels, correction.binaryLabels);
    }
    if (correction.manualLabels) {
      Object.assign(this.correctionState.manualLabels, correction.manualLabels);
    }
    // Restore timeline marker values from saved correction
    this.timelineStationaryAt = correction.stationaryAt ?? null;
    this.timelineDryAt = correction.dryAt ?? null;
    this.$emit("timelineValuesChanged", {
      stationaryValue: this.timelineStationaryAt?.relativeValue ?? null,
      dryValue: this.timelineDryAt?.relativeValue ?? null,
    });
  }

  /**
   * Marks a field as edited by the user.
   * The Save Corrections button is enabled when any field has been edited.
   */
  markFieldEdited(field: string) {
    this.editedFields = new Set([...this.editedFields, field]);
  }

  // ─── Actions ─────────────────────────────────────────────────────────

  /**
   * Saves user corrections to the backend.
   */
  async saveCorrections() {
    this.isSavingCorrection = true;

    const user = getActiveUser();
    if (!user) {
      this.showError("No active user");
      this.isSavingCorrection = false;
      return;
    }

    const correction: CorrectionPayload = {
      trafficLight: this.correctionState.trafficLight as TrafficLight,
      isDry: this.correctionState.isDry,
      isStationary: this.correctionState.isStationary,
      binaryLabels: { ...this.correctionState.binaryLabels } as BinaryLabelCorrections,
      manualLabels: { ...this.correctionState.manualLabels } as ManualLabels,
      stationaryAt: this.timelineStationaryAt,
      dryAt: this.timelineDryAt,
    };

    const request: SaveCorrectionRequest = {
      serialNo: this.serialNo,
      chainId: this.chainId,
      window: this.selectedWindow as PredictionWindow,
      userId: user.id,
      sourceExecutionId: this.currentExecutionId,
      sourceTrigger: this.activeView === "daily" ? "daily" : "manual",
      correction,
    };

    try {
      await AiPredictionApi.saveCorrection(request);
      this.showSuccess(this.$t("quality.messages.correctionSaved").toString());
      this.editedFields.clear();

      // Reload data to show updated corrections
      await this.loadPredictions();
    } catch (error) {
      if (error instanceof AiPredictionApiError) {
        this.showError(error.apiMessage);
      } else {
        this.showError(this.$t("quality.messages.correctionError").toString());
      }
    } finally {
      this.isSavingCorrection = false;
    }
  }

  /**
   * Runs a manual ML prediction with the selected windows.
   */
  async runManualPrediction() {
    if (!this.isPredictionFeatureActivated) {
      this.showError(this.$t("quality.messages.featureNotActivated").toString());
      return;
    }

    if (!this.isLatestModelSelected) {
      this.showError(this.$t("quality.messages.latestModelOnlyRecalculation").toString());
      return;
    }

    this.isManualPredictionRunning = true;
    this.showManualCalculationDialog = false;

    try {
      const windows = this.isRenoModel ? ["all" as const] : this.manualWindows;
      this.manualResult = await AiPredictionApi.runManualPrediction({
        serialNo: this.serialNo,
        chainId: this.chainId,
        windows,
      });

      this.showSuccess(this.$t("quality.messages.predictionComplete").toString());

      // Reload data to show the new manual result
      await this.loadPredictions();

      // Switch to manual view
      this.activeViewTab = 1;
    } catch (error) {
      if (error instanceof AiPredictionApiError) {
        this.showError(error.apiMessage);
      } else {
        this.showError(this.$t("quality.messages.predictionError").toString());
      }
    } finally {
      this.isManualPredictionRunning = false;
    }
  }

  /**
   * Routes calculation to backfill (R1.0+) or manual dialog (legacy models).
   */
  triggerCalculation() {
    if (!this.isPredictionFeatureActivated) {
      this.showError(this.$t("quality.messages.featureNotActivated").toString());
      return;
    }

    if (!this.isLatestModelSelected) {
      this.showError(this.$t("quality.messages.latestModelOnlyRecalculation").toString());
      return;
    }

    // Route on the actual latest model, not the picker value. When a chain has
    // no computed predictions yet, selectedModelVersion is still "" so
    // isSelectedModelReno would be false and wrongly open the legacy window
    // dialog for what is really a RENO (R1.0+) model. isRenoModel reads the
    // backend's latestAvailableModel and matches runManualPrediction's branch.
    if (this.isRenoModel) {
      this.runBackfill();
    } else {
      this.showManualCalculationDialog = true;
    }
  }

  /**
   * Start a backfill job to recalculate all daily predictions with the latest model.
   * Polls for progress and reloads predictions when complete.
   */
  async runBackfill() {
    if (!this.isPredictionFeatureActivated) {
      this.showError(this.$t("quality.messages.featureNotActivated").toString());
      return;
    }

    if (!this.isLatestModelSelected) {
      this.showError(this.$t("quality.messages.latestModelOnlyRecalculation").toString());
      return;
    }

    if (this.isBackfillRunning) return;
    this.isBackfillRunning = true;

    try {
      const result = await AiPredictionApi.startBackfill({
        serialNo: this.serialNo,
        chainId: this.chainId,
      });

      // Poll for completion
      this.backfillPollTimer = window.setInterval(async () => {
        try {
          const status = await AiPredictionApi.getBackfillStatus(result.jobId);
          if (status.status !== "running") {
            this.stopBackfillPolling();
            this.showSuccess(this.$t("quality.messages.backfillComplete").toString());
            // Reset to latest model so the hint disappears
            this.selectedModelVersion = "";
            await this.loadPredictions();
          }
        } catch {
          this.stopBackfillPolling();
          this.showError(this.$t("quality.messages.predictionError").toString());
        }
      }, 5000);
    } catch (error) {
      this.isBackfillRunning = false;
      if (error instanceof AiPredictionApiError) {
        this.showError(error.apiMessage);
      } else {
        this.showError(this.$t("quality.messages.predictionError").toString());
      }
    }
  }

  private stopBackfillPolling() {
    if (this.backfillPollTimer != null) {
      clearInterval(this.backfillPollTimer);
      this.backfillPollTimer = null;
    }
    this.isBackfillRunning = false;
  }

  // ─── Snackbar Helpers ────────────────────────────────────────────────

  private showSuccess(message: string) {
    this.successMessage = message;
    this.showSuccessSnackbar = true;
  }

  private showError(message: string) {
    this.errorMessage = message;
    this.showErrorSnackbar = true;
  }

  // ─── Timeline Marker Handling ────────────────────────────────────────

  /**
   * Called from parent (IresDeviceDetail) when user selects a timeline marker.
   */
  onTimelineMarkerUpdate(event: { field: "stationary" | "dry"; relativeValue: number; timestamp: firestore.Timestamp }) {
    const point: TimelineMarkerPoint = {
      relativeValue: event.relativeValue,
      timestamp: event.timestamp,
    };
    if (event.field === "stationary") {
      this.timelineStationaryAt = point;
    } else {
      this.timelineDryAt = point;
    }
    this.markFieldEdited("timelineMarker");
  }

  // ─── Close Handling (for content selector integration) ───────────────

  /**
   * Called by the parent when the content selector tries to close this view.
   * Returns true if it's safe to close (no unsaved edits).
   */
  async onCloseRating(): Promise<boolean> {
    if (!this.hasEdits) {
      return true;
    }
    // If there are unsaved edits, discard them and allow close
    this.editedFields.clear();
    this.initializeCorrectionState();
    return true;
  }
}
